"use client";

import { useLang } from "@/contexts/language-context";
import AnimatedSection from "./AnimatedSection";
import { Check } from "lucide-react";

export default function DualPricingSection() {
  const { t } = useLang();

  return (
    <section id="how-it-works" className="py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
        
        {/* Left: Phone/Receipt Mockup Card */}
        <AnimatedSection className="relative flex justify-center">
          {/* Soft background shape */}
          <div className="absolute inset-0 m-auto w-[80%] h-[90%] bg-redy-orange/5 rounded-[40px] -z-10 transform -rotate-3" />
          
          <div className="relative bg-white w-full max-w-[380px] rounded-[40px] p-8 shadow-[0_20px_60px_rgba(0,0,0,0.08)] border border-black/5">
            <div className="text-[12px] font-bold text-black/40 uppercase tracking-widest text-center mb-6">
              {t("Customer Checkout", "Checkout del Cliente")}
            </div>
            
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 bg-[#FAFAFA] rounded-xl flex items-center justify-center text-xl shadow-sm">🍔</div>
              <div>
                <div className="font-bold text-[15px] text-redy-charcoal">Double Cheeseburger</div>
                <div className="text-[13px] font-medium text-redy-secondary">Combo</div>
              </div>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex justify-between items-center p-4 rounded-[20px] bg-[#FAFAFA] border border-black/5 relative overflow-hidden group hover:border-redy-orange transition-colors cursor-pointer">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-redy-orange" />
                <div className="font-bold text-[15px] text-redy-charcoal">{t("Pay with Card", "Pagar con Tarjeta")}</div>
                <div className="text-[18px] font-black text-redy-charcoal">$16.99</div>
              </div>

              <div className="flex justify-between items-center p-4 rounded-[20px] bg-[#FAFAFA] border border-black/5 hover:border-redy-green transition-colors cursor-pointer">
                <div>
                  <div className="font-bold text-[15px] text-redy-charcoal">{t("Pay with Cash", "Pagar en Efectivo")}</div>
                  <div className="text-[11px] font-bold text-redy-green mt-1">{t("Cash discount (4%)", "Descuento en efectivo (4%)")}</div>
                </div>
                <div className="text-[18px] font-black text-redy-green">$16.31</div>
              </div>
            </div>

            <div className="flex items-start gap-3 bg-redy-orange/5 p-4 rounded-[20px]">
              <Check className="w-4 h-4 text-redy-orange mt-0.5 shrink-0" />
              <div className="text-[12px] font-bold text-redy-charcoal/80 leading-snug">
                {t("The card price is designed to cover the processing cost. Cash payers get the discount.", "El precio con tarjeta está pensado para cubrir el costo de procesamiento. Quien paga en efectivo recibe el descuento.")}
              </div>
            </div>
          </div>

          {/* Floating decorative elements */}
          <div className="absolute top-10 -right-8 bg-white px-4 py-3 rounded-2xl shadow-xl border border-black/5 flex items-center gap-3">
            <span className="text-2xl">⛽</span>
            <div className="text-[13px] font-bold leading-tight">Gas Station<br/>Model</div>
          </div>
        </AnimatedSection>

        {/* Right: Text Content */}
        <AnimatedSection delay={0.2}>
          <h2 className="text-4xl md:text-[52px] font-black tracking-tight leading-[1.05] text-redy-charcoal mb-6">
            {t("No tricks.", "Sin trucos.")}
            <br />
            <span className="text-black/30">
              {t("Here's exactly how it works.", "Así es exactamente como funciona.")}
            </span>
          </h2>
          <p className="text-[15px] font-medium text-redy-secondary leading-relaxed mb-10 max-w-[480px]">
            {t(
              "Just like a gas station: a card price and a cash price. The menu shows the card price, designed to cover the processing cost, and customers who pay cash get a discount. Rules vary by state, so check the signage yours requires.",
              "Justo como en la gasolinera: un precio con tarjeta y otro en efectivo. El menú muestra el precio con tarjeta, pensado para cubrir el costo de procesamiento, y quien paga en efectivo recibe un descuento. Las reglas cambian por estado, así que revisa los avisos que exige el tuyo."
            )}
          </p>

          <div className="space-y-8">
            {[
              {
                num: "01",
                title: { en: "Your menu shows the card price", es: "Tu menú muestra el precio con tarjeta" },
                desc: { en: "Everything works just like it does today. Nothing changes for your staff.", es: "Todo funciona igual que hoy. Nada cambia para tu equipo." }
              },
              {
                num: "02",
                title: { en: "At checkout, the customer chooses", es: "Al pagar, el cliente elige" },
                desc: { en: "Paying cash gets a discount — 4% by default — shown as its own line on the receipt.", es: "Pagar en efectivo recibe un descuento — 4% por defecto — que aparece como línea propia en el recibo." }
              },
              {
                num: "03",
                title: { en: "Card fees stop eating your margin", es: "Los fees de tarjeta dejan de comerse tu margen" },
                desc: { en: "The card price is designed to cover the processing cost, so it doesn't come out of your pocket. It's that simple.", es: "El precio con tarjeta está pensado para cubrir el costo de procesamiento, así no sale de tu bolsillo. Así de simple." }
              }
            ].map((step, i) => (
              <div key={i} className="flex gap-6 items-start">
                <div className="text-2xl font-black text-black/10 mt-1">{step.num}</div>
                <div>
                  <h3 className="text-[16px] font-bold text-redy-charcoal mb-2">{t(step.title.en, step.title.es)}</h3>
                  <p className="text-[14px] font-medium text-redy-secondary leading-relaxed">{t(step.desc.en, step.desc.es)}</p>
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.4} className="mt-10 flex justify-center lg:justify-start">
          <button
            onClick={() => document.getElementById("register")?.scrollIntoView({ behavior: "smooth" })}
            className="inline-flex items-center gap-2 text-[14px] font-bold text-white bg-redy-charcoal px-8 py-3.5 rounded-full hover:bg-black transition-all active:scale-95 shadow-sm cursor-pointer"
          >
            {t("Start for free — $0/month", "Empieza gratis — $0/mes")}
            <span className="text-lg leading-none">→</span>
          </button>
        </AnimatedSection>

      </div>
    </section>
  );
}
